import { App } from 'antd';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext, useSelfContext } from '@/context';
import { useDevice, useLocalStorage, useModal, useReserveVotables } from '@/hooks';
import { PageContainer, CreditButton } from '@/components/common';
import { SelfRatingModal } from '@/components/modals';
import ChangeSelfModal from './ChangeSelfModal';
import ReserveHeader from './ReserveHeader';
import ReserveProgramDesktop from './ReserveProgramDesktop';
import ReserveProgramMobile from './ReserveProgramMobile';
import usePrograms from './usePrograms';
import useWeekManagement from './useWeekManagement';
import ReserveDetailModal from './ReserveDetailModal';
import RateButton from '../RateButton';
import { envConfig } from '@/configs';

import './styles.scss';
import SelectReserveModeModal from './SelectReserveModeModal';

export interface ReserveProgramProps {
  variant: AppModules;
}

export function ReserveProgram({ variant }: ReserveProgramProps) {
  const conf = envConfig.onPremise;
  const [isNewReserveMode, setIsNewReserveMode] = useLocalStorage('new-reserve-mode-api', null);
  const navigate = useNavigate();
  const [locked, setLocked] = useState(false);
  const [t] = useTranslation(['common', 'reserve']);
  const { mode } = useAppContext();
  const { message } = App.useApp();
  const { isDesktop } = useDevice();
  const selfModal = useModal();
  const rateModal = useModal();
  const detailModal = useModal();
  const changeSelfModal = useModal();
  const selectReserveModeModal = useModal();
  const { currentSelf, selfText, redirectToSelf } = useSelfContext();
  const { programs, groupedPrograms, fetchPrograms, isProgramsFetching } = usePrograms();
  const { date, dates, dateEN, nextWeek, prevWeek } = useWeekManagement();
  const {
    submit,
    rateReasons,
    votableItems,
    isFetched,
    fetch: fetchVotable,
    loading: votableLoading,
  } = useReserveVotables();

  console.log('isNewReserveMode', isNewReserveMode);


  useEffect(() => {
    if (conf.core.changableReserveMode === true && currentSelf) {
      selectReserveModeModal.show();
    }
  }, [currentSelf]);

  const onRateButtonClick = useCallback(() => {
    if (votableItems.length === 0) {
      message.warning({
        key: 'not-votable-for-rate',
        content: t('reserve:errors.no-polls'),
      });
    }
    rateModal.show();
  }, [rateModal, votableItems]);

  const fetchCurrentSelfPrograms = useCallback(() => {
    fetchPrograms(currentSelf?.id, dateEN);
  }, [dateEN, fetchPrograms, currentSelf?.id]);

  useEffect(() => {
    fetchCurrentSelfPrograms();
  }, [fetchCurrentSelfPrograms]);

  useEffect(() => {
    if (currentSelf == null) {
      if (isDesktop()) {
        selfModal.show();
      }
      else {
        redirectToSelf({ mode, navigate });
      }
    }
  }, [currentSelf, isDesktop]);

  useEffect(() => {
    if (currentSelf && !isFetched) {
      fetchVotable().then(rateModal.show);
    }
  }, [currentSelf, isFetched]);

  const isMobile = !isDesktop();
  const rateButton = (
    <RateButton
      size={isMobile ? 'small' : 'middle'}
      onClick={onRateButtonClick}
    />
  );

  return (
    <PageContainer
      noPadding
      hideScrollbar={!isDesktop()}
      title={selfText('reserve-title')}
      desktopLeftElement={<ReserveHeader isNewReserveMode={isNewReserveMode} setIsNewReserveMode={setIsNewReserveMode} />}
      sideButton={isMobile ? <CreditButton /> : rateButton}
      bodyClassName={`reserve-page-${variant}`}
      containerClassName={isMobile ? '!overflow-hidden' : ''}
    >
      <SelfRatingModal
        type={variant}
        hide={rateModal.hide}
        open={rateModal.open}
        items={votableItems}
        reasons={rateReasons}
        loading={votableLoading}
        submit={submit}
      />
      <ReserveDetailModal variant={variant} {...detailModal} />
      <ChangeSelfModal
        variant={variant}
        onSuccess={fetchCurrentSelfPrograms}
        {...changeSelfModal}
      />
      {
        isNewReserveMode === null && (
          <SelectReserveModeModal
            hide={selectReserveModeModal.hide}
            open={selectReserveModeModal.open}
            setIsNewReserveMode={setIsNewReserveMode}
          />
        )
      }

      {currentSelf && conf.core.changableReserveMode === true ? (
        <>
          {isMobile ? (
            <>
              <ReserveHeader mobileButton={rateButton} isNewReserveMode={isNewReserveMode} setIsNewReserveMode={setIsNewReserveMode} />
              <ReserveProgramMobile
                isNewReserveMode={isNewReserveMode}
                date={date}
                dates={dates}
                variant={variant}
                programs={programs}
                nextWeek={nextWeek}
                prevWeek={prevWeek}
                locked={locked}
                setLocked={setLocked}
                detailModal={detailModal}
                changeSelfModal={changeSelfModal}
                isProgramsFetching={isProgramsFetching}
              />
            </>
          ) : (
            <ReserveProgramDesktop
              isNewReserveMode={isNewReserveMode}
              variant={variant}
              programs={programs}
              groupedPrograms={groupedPrograms}
              nextWeek={nextWeek}
              prevWeek={prevWeek}
              detailModal={detailModal}
              changeSelfModal={changeSelfModal}
              isProgramsFetching={isProgramsFetching}
              locked={locked}
              setLocked={setLocked}
            />
          )}
        </>
      ) : (
        <>
          {isMobile ? (
            <>
              <ReserveHeader mobileButton={rateButton} isNewReserveMode={false} setIsNewReserveMode={() => { }} />
              <ReserveProgramMobile
                date={date}
                dates={dates}
                variant={variant}
                programs={programs}
                nextWeek={nextWeek}
                prevWeek={prevWeek}
                locked={locked}
                setLocked={setLocked}
                detailModal={detailModal}
                changeSelfModal={changeSelfModal}
                isProgramsFetching={isProgramsFetching}
              />
            </>
          ) : (
            <ReserveProgramDesktop
              variant={variant}
              programs={programs}
              groupedPrograms={groupedPrograms}
              nextWeek={nextWeek}
              prevWeek={prevWeek}
              detailModal={detailModal}
              changeSelfModal={changeSelfModal}
              isProgramsFetching={isProgramsFetching}
              locked={locked}
              setLocked={setLocked}
            />
          )}
        </>
      )
      }
    </PageContainer>
  );
}

export default ReserveProgram;
